import { DateTime } from 'luxon';

/** Visible helper beside every operator datetime-local that is a UTC wall clock. */
export const UTC_INPUT_HINT = 'Input in UTC time';

const operatorZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/**
 * datetime-local has no zone. The clock the operator types IS the UTC instant:
 * `2026-09-23T05:10` is `2026-09-23T05:10:00.000Z`, never that clock in the
 * browser's zone.
 */
export const utcInputToIso = value => {
  if (value == null || value === '') return undefined;
  const dt = DateTime.fromISO(String(value), { zone: 'utc' });
  if (!dt.isValid) return undefined;
  return dt.toUTC().toJSDate().toISOString();
};

/** Stored UTC ISO → the same UTC clock face for a datetime-local control. */
export const isoToUtcInput = iso => {
  if (!iso) return '';
  const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
  if (!dt.isValid) return '';
  return dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm");
};

/** `YYYY-MM-DD HH:mm` in UTC, for lists and helpers that must match the picker. */
export const showUtcWhen = iso => {
  const face = isoToUtcInput(iso);
  return face ? face.replace('T', ' ') : '—';
};

/** `YYYY-MM-DD` in UTC, where the operator surface only ever showed a date. */
export const showUtcDate = iso => {
  if (!iso) return '—';
  const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
  if (!dt.isValid) return '—';
  return dt.toUTC().toFormat('yyyy-MM-dd');
};

/** Operator-zone clock, display only. Empty when the instant cannot be read. */
export const showLocalWhen = iso => {
  if (!iso) return '';
  const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
  if (!dt.isValid) return '';
  return dt.setZone(operatorZone()).toFormat('yyyy-MM-dd HH:mm');
};

/**
 * UTC clock with a UTC label. When the operator zone's wall clock differs,
 * the local face is a secondary only — omitted when it would repeat UTC.
 * `2026-09-23 05:10 UTC` or `2026-09-23 05:10 UTC (local 2026-09-22 22:10)`.
 */
export const showUtcClock = iso => {
  const utc = showUtcWhen(iso);
  if (utc === '—') return utc;
  const local = showLocalWhen(iso);
  if (!local || local === utc) return `${utc} UTC`;
  return `${utc} UTC (local ${local})`;
};

/** `yyyy-MM-dd` of the current UTC day, for date pickers capped at "today". */
export const utcToday = () => DateTime.utc().toFormat('yyyy-MM-dd');

/**
 * A stored instant is on or after the UTC calendar day of `yyyy-MM-dd`.
 * `2026-09-23` is `2026-09-23T00:00:00.000Z`, not midnight in the browser zone.
 * An empty day does not filter.
 */
export const isOnOrAfterUtcDay = (iso, day) => {
  if (!day) return true;
  const start = DateTime.fromISO(String(day), { zone: 'utc' }).startOf('day');
  if (!start.isValid) return true;
  const instant = DateTime.fromISO(String(iso), { zone: 'utc' });
  if (!instant.isValid) return false;
  return instant.toMillis() >= start.toMillis();
};

/**
 * UTC clock range, plus the operator's local clocks when they differ.
 * `2026-09-22 06:38 – 2026-09-23 21:38 UTC (local 2026-09-21 23:38 – 2026-09-23 14:38)`
 */
export const formatUtcWindow = (startIso, endIso) => {
  const start = showUtcWhen(startIso);
  const end = showUtcWhen(endIso);
  const utc = `${start} – ${end} UTC`;
  const localStart = showLocalWhen(startIso);
  const localEnd = showLocalWhen(endIso);
  if (!localStart || !localEnd || (localStart === start && localEnd === end)) return utc;
  return `${utc} (local ${localStart} – ${localEnd})`;
};

/** Minute resolution, matching datetime-local. Seconds on a stored bound do not
 *  push a reloaded picker minute outside the window. */
const utcMinuteMs = iso => {
  const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
  return dt.isValid ? dt.startOf('minute').toMillis() : NaN;
};

/**
 * A datetime-local UTC wall value is inside [startIso, endIso]. An empty
 * value is not a window violation (the form requires both fields separately).
 * Bounds are real instants, so a minute the operator types is not shifted
 * into the browser zone before the comparison. Both sides are compared at
 * minute resolution, the same face the picker can hold.
 */
export const utcInputWithinIsoRange = (value, startIso, endIso) => {
  if (!value) return true;
  const iso = utcInputToIso(value);
  if (!iso) return false;
  const ms = utcMinuteMs(iso);
  if (Number.isNaN(ms)) return false;
  if (startIso) {
    const start = utcMinuteMs(startIso);
    if (!Number.isNaN(start) && ms < start) return false;
  }
  if (endIso) {
    const end = utcMinuteMs(endIso);
    if (!Number.isNaN(end) && ms > end) return false;
  }
  return true;
};

/** Current UTC minute as a datetime-local value, optionally shifted. */
export const utcInputFromNow = (duration = {}) =>
  DateTime.utc().plus(duration).toFormat("yyyy-MM-dd'T'HH:mm");
